<template>
  <BaseReadCardWidget
    heading="Facility Details"
    entity-label="Facility"
    @edit-click="handleEdit"
    :edit-auth-request="updateFacilityAR"
    @delete-click="deleteFacilitySite"
    :delete-auth-request="deleteFacilityAR"
    :deleteCondition="deleteCondition"
    related-entities-label=", material accepted, affiliations, users, and internal notes"
    :hide-default-actions="false"
    :no-data="!facilitySiteStore.facilitySite"
    @remove="$emit('remove')"
  >
    <template #default v-if="facilitySiteStore.facilitySite">
      <ReadField label="Short Name" :val="facilitySiteStore.facilitySite.shortName" />
      <ReadField label="Permit ID" :val="facilitySiteStore.facilitySite.number" />
      <ReadField
        label="Type"
        :val="facilitySiteStore.facilitySite.typeMurn"
        :lookup-format="LookupFormat.description"
      />
      <ReadField label="Website" :val="facilitySiteStore.facilitySite.website" />

      <ReadField label="Owner Name" :val="facilitySiteStore.facilitySite.ownerName" />

      <ReadField label="Operator Name" :val="facilitySiteStore.facilitySite.operatorName" />

      <ReadFieldBase label="Physical Status">
        <Badge
          :murn="facilitySiteStore.facilitySite.physicalStatusMurn"
          :lookup-format="LookupFormat.description"
          v-if="facilitySiteStore.facilitySite.physicalStatusMurn"
        ></Badge>
        <span v-if="!facilitySiteStore.facilitySite.physicalStatusMurn">—</span>
      </ReadFieldBase>
      <ReadFieldBase label="Permit Status">
        <Badge
          :murn="facilitySiteStore.facilitySite.permitStatusMurn"
          :lookup-format="LookupFormat.description"
          v-if="facilitySiteStore.facilitySite.permitStatusMurn"
        ></Badge>
        <span v-if="!facilitySiteStore.facilitySite.permitStatusMurn">—</span>
      </ReadFieldBase>

      <ReadField label="Is Recycler?" :val="facilitySiteStore.facilitySite.isRecycler" />
      <ReadField label="Subject to Fee?" :val="facilitySiteStore.facilitySite.subjectToFee" />
    </template>
    <!-- Optional: Use status slot for badges -->
    <!-- <template #status v-if="data?.statusBadge">
            <Badge :label="data.statusBadge" />
          </template> -->
  </BaseReadCardWidget>
</template>

<script setup lang="ts">
import { routeNames } from "@/routes";
import { useFacilitySiteStore } from "@/views/facilitySite/facilitySiteStore";
import { LookupFormat, ReadField, ReadFieldBase } from "@modernary/mui/components/read";
import { BaseReadCardWidget } from "@modernary/mui/components/widgetGrid";
import { AuthRequest, getUpdateResource } from "@modernary/mui/utils/authorization";
import { formatAddress } from "@modernary/mui/utils/filters";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { canDeleteFacilitySite, deleteFacilitySiteById } from "@/services/endpoints";
import { useApi } from "@modernary/mui/utils/api";
import { Badge } from "@modernary/mui/components/badge";

defineEmits<{
  remove: [];
}>();

/**
 * READ THIS BEFORE CHANGING EDIT OR DELETE FEATURES:
 * Some "Hub" features are TBD, so we're currently *copying* edit and delete features in both files.
 * Keep them in sync!
 */

const router = useRouter();
const route = useRoute();
const facilitySiteStore = useFacilitySiteStore();

if (!facilitySiteStore.facilitySite) {
  throw new Error("Facility site not found");
}

const { exec: execDeleteFacilitySite } = useApi(deleteFacilitySiteById, {
  responseAdapter: (response) => {
    router.push({ name: routeNames.facilitySite.list });
    return response.data;
  },
});

function deleteFacilitySite() {
  execDeleteFacilitySite(route.params.id);
}

const { exec: fetchCanDeleteFacility } = useApi(canDeleteFacilitySite, {});

const deleteCondition = () => {
  return fetchCanDeleteFacility(facilitySiteStore.facilitySite?.id).then((response) => {
    if (response?.canDelete) {
      return null;
    } else {
      let preventativeItems = [];
      let message = "This facility has ";
      if (response?.applicationsCount && response.applicationsCount > 0) {
        preventativeItems.push(
          response?.applicationsCount + " application" + (response?.applicationsCount > 1 ? "s" : ""),
        );
      }
      if (response?.permitsCount && response.permitsCount > 0) {
        preventativeItems.push(response?.permitsCount + " permit" + (response?.permitsCount > 1 ? "s" : ""));
      }
      if (response?.inspectionsCount && response.inspectionsCount > 0) {
        preventativeItems.push(
          response?.inspectionsCount + " inspection" + (response?.inspectionsCount > 1 ? "s" : ""),
        );
      }
      if (response?.complaintsCount && response.complaintsCount > 0) {
        preventativeItems.push(response?.complaintsCount + " complaint" + (response?.complaintsCount > 1 ? "s" : ""));
      }
      if (response?.disposalReportsCount && response.disposalReportsCount > 0) {
        preventativeItems.push(
          response?.disposalReportsCount + " disposal report" + (response?.disposalReportsCount > 1 ? "s" : ""),
        );
      }

      message += preventativeItems.join(", ").replace(/, ([^,]*)$/, ", and $1") + ".";
      return message;
    }
  });
};

function handleEdit() {
  router.push({
    name: routeNames.facilitySite.edit,
    params: { id: route.params.id },
  });
}

const updateFacilityAR = new AuthRequest();
const deleteFacilityAR = new AuthRequest();

watch(
  () => facilitySiteStore.facilitySite,
  (newValue, oldValue) => {
    if (!newValue?.id) return;

    updateFacilityAR.permissionName = "UpdateFacilitySite";
    updateFacilityAR.resource = getUpdateResource(facilitySiteStore.authResource);

    deleteFacilityAR.permissionName = "DeleteFacilitySite";
    deleteFacilityAR.resource = facilitySiteStore.authResource;
  },
  { immediate: true },
);
</script>
